import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isMobile } from 'react-device-detect';
import { toastr } from 'react-redux-toastr';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
// Material-UI imports
import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Grid as MaterialGrid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  FileCopy,
  BarChart,
  Edit,
  Delete,
  Send,
  Queue,
  Stop,
  Replay,
  ToggleOn,
} from '@material-ui/icons';
import { Eye } from 'react-feather';

// PrimeReact imports
import { SelectButton } from 'primereact/selectbutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import 'primereact/resources/themes/nova/theme.css';
import 'primereact/resources/primereact.css';

// Local imports
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import PrimaryButton from 'common/components/buttons/primaryButton';
import ConfirmationCard from 'common/components/cards/confirmationCard/confirmationCard';
import GenericConfirmationCard from 'common/components/cards/genericConfirmationModel/genericConfirmationModel';
import MaterialCard from 'common/components/cards/materialCard';
import { ActionNameElement } from 'common/components/table/tableComponents';
import placeholder_illustration from 'assets/img/placeholder_illustration.png';
import Engagement from 'assets/titlesMobile/engagement.svg';

// Action imports
import { getTemplates } from '../actionsFile';
import { deleteActionDraft, onClickDraftAction } from '../actionsDrafts/actionsDraftsScripts';
import { deleteActionSuspended, reactivateAction } from '../actionsHistory/actionsHistoryActions';
import {
  duplicateAction,
  onClickAction,
  onClickActionResults,
  onClickActionShare,
  suspendActivity,
} from '../activeActions/activeActionsScripts';
import { isMultiStationChildAction } from '../helpers/clubsMultiStation';

// Components
import ReactivateAction from '../actionsHistory/reactivateForm';
import { VouchersEmLoteModal } from 'modules/owner/pages/vouchers';
import VendasOnlineParceiroResultsModal from '../vendasOnlineParceiro/components/ResultsModal';

// Services
import { useActionsService } from './hooks/useActionsService';

// Constants
const ACTION_TYPES = {
  ONLINE: 'online',
  SCHEDULED: 'scheduled',
  DRAFTS: 'drafts',
  CLOSED: 'closed',
};

const OPTIONS = [
  { label: 'Online', value: ACTION_TYPES.ONLINE },
  { label: 'Programadas', value: ACTION_TYPES.SCHEDULED },
  { label: 'Rascunho', value: ACTION_TYPES.DRAFTS },
  { label: 'Encerradas', value: ACTION_TYPES.CLOSED },
];

const INITIAL_LAZY_STATE = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: null,
  sortOrder: null,
  filters: {
    name: { value: '', matchMode: 'contains' },
    createdAt_like: { value: null, matchMode: 'like' },
  },
};

// Styles
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'contents',
    '& .p-datatable-tbody': {
      display: 'none',
    },
    '& .p-column-header-content': {
      display: 'none',
    },
  },
  container: {
    maxWidth: '86vw',
    paddingBottom: '4rem',
  },
  titleContainer: {
    display: 'flex',
    marginBottom: 15,
    marginTop: 15,
  },
  templatesContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflowX: 'scroll',
    overflowY: 'hidden',
    marginBottom: '.7rem',
  },
  tableContainer: {
    marginTop: isMobile ? 0 : 15,
    marginBottom: isMobile ? 0 : 15,
  },
  selectButton: {
    width: '100%',
    marginBottom: 10,
    color: 'black',
    backgroundColor: 'white',
    background: 'white',
    outlineColor: 'white',
    borderBlockEndColor: 'white',
  },
  mobileTitle: {
    fontSize: 20,
    fontWeight: 400,
    margin: 0,
    marginLeft: 15,
  },
  iconContainer: {
    width: 23,
    height: 23,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  loadingContainer: {
    display: 'flex',
    width: '100vw',
    justifyContent: 'center',
    alignContent: 'center',
    marginTop: theme.spacing(7),
  },
}));

// Main Component
function GeneralActions({
  auth,
  actions,
  general,
  router,
  getTemplates,
  onClickAction,
  onClickActionResults,
  onClickActionShare,
  onClickDraftAction,
  suspendActivity,
  duplicateAction,
  deleteActionSuspended,
  dispatch,
}) {
  const classes = useStyles();

  // State
  const [selectedOption, setSelectedOption] = useState(ACTION_TYPES.ONLINE);
  const [lazyState, setLazyState] = useState(INITIAL_LAZY_STATE);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openDuplicateBackDrop, setOpenDuplicateBackDrop] = useState(false);
  const [openModalLote, setOpenModalLote] = useState(false);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  // Item states
  const [template, setTemplate] = useState({});
  const [item, setItem] = useState(null);
  const [selectedRow, setSelectedRow] = useState();
  const [reactiveRow, setReactiveRow] = useState();
  const [resultsActivity, setResultsActivity] = useState(null);

  // Custom hook for actions service
  const { data, totalRecords, loading, buttonLoading, handleDeleteDraft, refreshData } =
    useActionsService(auth, selectedOption, lazyState);

  // Get station ID
  const stationId = useMemo(() => {
    return auth?.user?.establishments?.[0]?.stations?.[0]?.id;
  }, [auth]);

  // Initialize component
  useEffect(() => {
    if (stationId) {
      getTemplates(auth.user.establishments[0].id);
    }
  }, [stationId, getTemplates, auth.user.establishments]);

  useEffect(() => {
    const buscarDadosEMostrarNotificacao = async () => {
      const options = {
        onToastrClick: () => router.push('/owner/acoes/minhas-atividades-validade'),
        timeOut: 0,
        showCloseButton: true,
      };
      try {
        const contagem = await buscarAtividadesPorDia(stationId, USER_TOKEN);
        let mensagem;
        let titulo;
        if (contagem === 1) {
          // Caso para exatamente UMA atividade
          mensagem = '1 atividade vencendo nos próximos 5 dias. Clique para ver.';
        } else if (contagem > 1) {
          // Caso para MAIS DE UMA atividade
          mensagem = `Tem ${contagem} atividades vencendo nos próximos 5 dias. Clique para ver.`;
        } else {
          // Caso para NENHUMA atividade (contagem === 0)
          mensagem = 'Nenhuma atividade próxima do vencimento.';
        }
        // Chama o toastr uma única vez com as variáveis definidas
        toastr.info(mensagem, options);
      } catch (error) {
        console.error('Falha ao buscar dados para notificação:', error);
        toastr.error('Não foi possível carregar suas atividades.', 'Erro');
      }
    };

    buscarDadosEMostrarNotificacao();
  }, [router]);

  // Reset pagination when option changes
  useEffect(() => {
    if (selectedOption) {
      setLazyState(INITIAL_LAZY_STATE);
      setPage(0);
    }
  }, [selectedOption]);

  // Event handlers
  const handleOptionChange = useCallback((e) => {
    setSelectedOption(e.value);
  }, []);

  const handleFilter = useCallback((event) => {
    setPage(0);
    const newState = { ...event, first: 0, page: 0 };
    setLazyState(newState);
  }, []);

  const handlePageChange = useCallback(
    (_event, newPage) => {
      setPage(newPage);
      setLazyState((prevState) => ({
        ...prevState,
        first: newPage * rowsPerPage,
        page: newPage,
      }));
    },
    [rowsPerPage]
  );

  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLazyState((prevState) => ({
      ...prevState,
      first: 0,
      rows: newRowsPerPage,
    }));
  }, []);

  const handleDataTablePage = useCallback((event) => {
    setLazyState(event);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (item) {
      await handleDeleteDraft(item);
      setOpenConfirmation(false);
      setItem(null);
    }
  }, [item, handleDeleteDraft]);

  // Template functions
  const itemTemplate = useCallback(
    (option) => <div style={{ paddingRight: '10px', paddingLeft: '10px' }}>{option.label}</div>,
    []
  );

  const dateBodyTemplateStart = useCallback((rowData) => <span>{rowData.startDate}</span>, []);

  const dateBodyTemplateEnd = useCallback((rowData) => <span>{rowData.endDate}</span>, []);

  const categoriesTemplate = useCallback((rowData) => {
    const categoryNamesString =
      rowData?.category_actions
        ?.map((categoryAction) => categoryAction.category?.name)
        .join(', ') || 'Sem categoria';
    return <span>{categoryNamesString}</span>;
  }, []);

  const nameBodyTemplate = useCallback(
    (rowData) => (
      <>
        <ActionNameElement
          className="p-column-filter"
          singleColum={false}
          actionName={rowData.name}
          image={rowData.image}
        />
        {rowData.journey_only ? (
          <span
            style={{
              display: 'inline-block',
              marginTop: -4,
              marginLeft: (rowData.image ? 62 : 0) + (isMobile ? 10 : 12),
              backgroundColor: '#EEF5FD',
              color: '#2D95EF',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 12,
              whiteSpace: 'nowrap',
            }}>
            Exclusiva de jornada
          </span>
        ) : null}
      </>
    ),

    []
  );

  // Action button components
  const ActionButton = useCallback(
    ({ title, icon: Icon, onClick, ...props }) => (
      <Tooltip title={title}>
        <IconButton
          aria-label={title}
          style={{ backgroundColor: 'transparent' }}
          onClick={onClick}
          {...props}>
          <Icon />
        </IconButton>
      </Tooltip>
    ),
    []
  );

  const getActionButtons = useCallback(
    (rowData) => {
      const isChild = isMultiStationChildAction(rowData);
      const buttons = {
        edit: isChild ? null : (
          <ActionButton
            key="edit"
            title="Editar"
            icon={Edit}
            onClick={() => onClickAction(rowData, router, selectedOption)}
          />
        ),
        editDraft: isChild ? null : (
          <ActionButton
            key="editDraft"
            title="Editar"
            icon={Edit}
            onClick={() => onClickDraftAction(rowData, router)}
          />
        ),
        duplicate: (
          <ActionButton
            key="duplicate"
            title="Duplicar"
            icon={FileCopy}
            onClick={() => duplicateAction(rowData, router, setOpenDuplicateBackDrop)}
          />
        ),
        results: (
          <ActionButton
            key="results"
            title="Ver resultados"
            icon={BarChart}
            onClick={() => {
              if (rowData.type_id === 22 || rowData.type_id === 23) {
                setResultsActivity(rowData);
                return;
              }
              onClickActionResults(rowData, router, selectedOption);
              sessionStorage.removeItem('currentPage');
            }}
          />
        ),
        delete: isChild ? null : (
          <ActionButton
            key="delete"
            title="Deletar"
            icon={Delete}
            onClick={() => {
              setOpenConfirmation(true);
              setItem(rowData.id);
            }}
          />
        ),
        reactivate: isChild ? null : (
          <ActionButton
            key="reactivate"
            title="Reativar"
            icon={Replay}
            onClick={() => {
              setVisible(true);
              setReactiveRow(rowData);
            }}
          />
        ),
        suspend: isChild ? null : (
          <ActionButton
            key="suspend"
            title="Encerrar"
            icon={Stop}
            onClick={() => suspendActivity(rowData.id, router, true)}
          />
        ),
        share: (
          <ActionButton
            key="share"
            title="Divulgar"
            icon={Send}
            onClick={() => onClickActionShare(rowData, router)}
          />
        ),
        vouchers: (
          <ActionButton
            key="vouchers"
            title="Gerar Vouchers em Lote"
            icon={Queue}
            onClick={() => {
              if (rowData.type_id === 43 || rowData.type_id === 44) {
                toastr.warning(
                  'Opção indisponível',
                  'Gerar Vouchers em Lote não está disponível para este tipo de atividade.'
                );
                return;
              }

              setSelectedRow(rowData);
              setOpenModalLote(true);
            }}
          />
        ),
        activate: isChild ? null : (
          <ActionButton
            key="activate"
            title="Ativar agora"
            icon={ToggleOn}
            onClick={() => onClickAction(rowData, router, 'scheduled')}
          />
        ),
        view: (
          <ActionButton
            key="view"
            title="Visualizar"
            icon={Eye}
            onClick={() => onClickAction(rowData, router, 'closed')}
          />
        ),
      };

      // Filhas multi-station: só o pai edita, pausa, apaga e reativa
      const buttonConfigs = {
        [ACTION_TYPES.ONLINE]: isChild
          ? ['results', 'share', 'vouchers']
          : ['edit', 'duplicate', 'results', 'suspend', 'share', 'vouchers'],
        [ACTION_TYPES.DRAFTS]: isChild ? [] : ['editDraft', 'duplicate', 'delete'],
        [ACTION_TYPES.SCHEDULED]: isChild ? [] : ['edit', 'suspend', 'activate'],
        [ACTION_TYPES.CLOSED]: isChild
          ? ['view', 'results']
          : ['view', 'duplicate', 'results', 'delete', 'reactivate'],
      };

      return (
        <div>
          {buttonConfigs[selectedOption]?.map((buttonKey) => buttons[buttonKey]).filter(Boolean) ||
            null}
        </div>
      );
    },
    [
      selectedOption,
      onClickAction,
      onClickDraftAction,
      onClickActionResults,
      onClickActionShare,
      suspendActivity,
      duplicateAction,
      router,
    ]
  );

  const buscarAtividadesPorDia = async (stationId, token) => {
    const url = `${BASE_API}/atividades-dias?station_id=${stationId}`;
    const headers = {
      Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
      'Content-Type': 'application/json',
    };
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      return data.count;
    } catch (error) {
      console.error('Falha ao buscar atividades:', error);
      throw error;
    }
  };
  // Render functions
  const renderEmptyScreen = useCallback(
    () => (
      <section id="activeActions">
        <GeneralGrid container spacing={3} alignItems="center">
          <GeneralGrid item xs={12} md={6}>
            <GeneralGrid container spacing={3}>
              <GeneralGrid item xs={12}>
                <div className="pl-4">
                  <h2 className="signup-text">Você ainda não possui Atividades Aprovadas</h2>
                  <p>Atividades são campanhas que você poderá distribuir para seus associados.</p>
                  <PrimaryButton onClick={() => router.push('/owner/acoes')} color="primary">
                    Criar Atividade
                  </PrimaryButton>
                </div>
              </GeneralGrid>
            </GeneralGrid>
          </GeneralGrid>
          <GeneralGrid item xs={12} md={6}>
            <img src={placeholder_illustration} className="img-fluid" alt="Ilustração Cupons" />
          </GeneralGrid>
        </GeneralGrid>
      </section>
    ),
    [router]
  );

  const renderTemplatesList = useCallback(() => {
    if (actions.templatesLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <LoadingProgress />
        </Box>
      );
    }

    let templatesBase = actions?.templatesList?.filter((item) => parseInt(item.category) === 1);

    if (stationId !== 1) {
      templatesBase = templatesBase?.filter((item) => item.type_action_id !== 43);
    }

    if (!templatesBase?.length) return null;

    return templatesBase.map((item, index) => (
      <GeneralGrid item xs={12} md={3} key={index} style={{ marginBottom: 10 }}>
        <MaterialCard
          horizontalCard
          onClick={
            item.has_bought
              ? () => {
                  setOpen(true);
                  setTemplate(item);
                }
              : () => router.push(`/owner/acoes/${item.id}`)
          }
          name={item.name}
          has_bought={item.has_bought}
          image={`${process.env.REACT_APP_S3_URL}/${item.owner_image || item.image}`}
          buttonLabel={item.has_bought ? 'Ativar' : 'Veja Mais'}
          type={item.type.name}
        />
      </GeneralGrid>
    ));
  }, [
    actions.templatesLoading,
    actions.templatesList,
    router,
    classes.loadingContainer,
    stationId,
  ]);

  const renderEmptyCard = useCallback(
    () => (
      <MaterialGrid item xs={12} md={3} style={{ marginBottom: 10 }}>
        <MaterialCard
          horizontalCard
          router={router}
          cardEmpty
          onClick={() => router.push('/owner/acoes')}
          name="Galeria de Atividades"
          image=""
          type=""
        />
      </MaterialGrid>
    ),
    [router]
  );

  const renderMobileTitle = useCallback(() => {
    if (!isMobile) return null;

    return (
      <div className="d-flex justify-content-start align-items-center container-fluid">
        <div className={classes.iconContainer}>
          <img src={Engagement} alt="engagement" style={{ width: 23, height: 'auto' }} />
        </div>
        <h4 className={classes.mobileTitle}>Engajamento</h4>
      </div>
    );
  }, [classes.iconContainer, classes.mobileTitle]);

  // Early returns
  if (!loading && actions.templatesList.length < 1 && !actions.templatesLoading) {
    return renderEmptyScreen();
  }

  return (
    <MaterialGrid container justifyContent="center" className={classes.container}>
      {/* Confirmation Modals */}
      <GenericConfirmationCard
        open={openConfirmation}
        loading={buttonLoading}
        cancelClose={() => setOpenConfirmation(false)}
        general={general}
        title="Deletar rascunho"
        confirmButtonColor="primary"
        text="Tem certeza que deseja deletar esse rascunho? Essa ação não poderá ser desfeita"
        onClose={() => setOpenConfirmation(false)}
        onClick={handleConfirmDelete}
      />

      {/* Title Section */}
      <GeneralGrid container justifyContent="center" spacing={2} className={classes.titleContainer}>
        {renderMobileTitle()}
      </GeneralGrid>

      {/* Templates Section */}
      <GeneralGrid container spacing={2} className={classes.templatesContainer}>
        {!isMobile && renderEmptyCard()}
        {renderTemplatesList()}
      </GeneralGrid>

      {/* Table Section */}
      <MaterialGrid container className={classes.tableContainer}>
        <div>
          <SelectButton
            value={selectedOption}
            onChange={handleOptionChange}
            optionLabel="label"
            options={OPTIONS}
            allowEmpty={false}
            className={classes.selectButton}
            itemTemplate={itemTemplate}
          />
        </div>

        <div className={classes.root}>
          <DataTable
            value={data}
            lazy
            totalRecords={totalRecords}
            rows={lazyState.rows}
            rowsPerPageOptions={[10, 25, 50]}
            dataKey="id"
            scrollable
            loading={loading}
            first={lazyState.first}
            onFilter={handleFilter}
            onPage={handleDataTablePage}
            resizableColumns
            filterDisplay="row"
            filters={lazyState.filters}
            style={{ width: '100%' }}>
            <Column
              field="name"
              header="Nome"
              sortable
              filter
              filterPlaceholder="Pesquisar"
              body={nameBodyTemplate}
              style={{ minWidth: '14rem', minHeight: '45rem' }}
            />
            <Column
              field="createdAt_like"
              header="Data de Criação"
              filter
              filterField="createdAt_like"
              filterMatchMode="like"
              filterElement={(options) => {
                return (
                  <Calendar
                    value={options.value}
                    onChange={(e) => {
                      const formattedDate = e.value ? e.value.toISOString().split('T')[0] : null;
                      options.filterApplyCallback(formattedDate);
                    }}
                    placeholder="Selecionar data"
                    dateFormat="yy-mm-dd"
                    showIcon
                    style={{ width: '100%' }}
                  />
                );
              }}
              style={{ minWidth: '12rem' }}
            />
          </DataTable>
        </div>
      </MaterialGrid>

      {/* Material Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Início da Publicação</TableCell>
              <TableCell>Fim da Publicação</TableCell>
              <TableCell>Categorias</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{nameBodyTemplate(row)}</TableCell>
                <TableCell>{dateBodyTemplateStart(row)}</TableCell>
                <TableCell>{dateBodyTemplateEnd(row)}</TableCell>
                <TableCell>{categoriesTemplate(row)}</TableCell>
                <TableCell>{getActionButtons(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Linhas por página"
        />
      </TableContainer>

      {/* Additional Modals */}
      <ReactivateAction
        visible={visible}
        onClose={() => setVisible(false)}
        router={router}
        actionData={reactiveRow}
      />

      <ConfirmationCard
        onClose={() => setOpen(false)}
        open={open}
        item={template.id}
        station_id={stationId}
        partner_id={null}
        sponsored={null}
        router={router}
      />

      <VouchersEmLoteModal
        atividade={selectedRow}
        visible={openModalLote}
        onClose={() => setOpenModalLote(false)}
      />

      <VendasOnlineParceiroResultsModal
        open={!!resultsActivity}
        activity={resultsActivity}
        onClose={() => setResultsActivity(null)}
      />

      <Backdrop className={classes.backdrop} open={openDuplicateBackDrop}>
        <CircularProgress color="inherit" />
        <span style={{ marginLeft: 10 }}>Gerando duplicação da atividade...</span>
      </Backdrop>
    </MaterialGrid>
  );
}

GeneralActions.propTypes = {
  actions: PropTypes.shape({
    templatesList: PropTypes.arrayOf(PropTypes.object),
    templatesLoading: PropTypes.bool,
  }),
  auth: PropTypes.shape({
    user: PropTypes.shape({
      establishments: PropTypes.array,
    }),
  }),
  deleteActionSuspended: PropTypes.func,
  duplicateAction: PropTypes.func,
  general: PropTypes.object,
  getTemplates: PropTypes.func,
  onClickAction: PropTypes.func,
  onClickActionResults: PropTypes.func,
  onClickActionShare: PropTypes.func,
  onClickDraftAction: PropTypes.func,
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  suspendActivity: PropTypes.func,
  dispatch: PropTypes.func,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  actions: state.actions,
  general: state.general,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onClickActionResults,
      onClickActionShare,
      onClickAction,
      suspendActivity,
      onClickDraftAction,
      getTemplates,
      reactivateAction,
      deleteActionSuspended,
      duplicateAction,
      dispatch: (action) => dispatch(action),
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(GeneralActions);
